import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryStack } from 'victory';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import useElementSize from 'common/hooks/useElementSize';
import { EcoDrivingRankingDashboardDto } from 'api/dashboard/dtos/EcoDrivingRankingDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './EcoDrivingRanking.module.scss';

const SUCCESS_COLOR = '#73C25C';
const WARNING_COLOR = '#F3A70D';
const DANGER_COLOR = '#D22222';

const getInitials = (name: string) =>
	name.split(/\s+/).filter(Boolean).map(w => (w[0] ?? '').toUpperCase() + '.').join('');

type SeriesKey = 'success' | 'warning' | 'danger';
type TooltipState = { driver: string; success: number; warning: number; danger: number; x: number; y: number };

const EcoDrivingRanking = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<EcoDrivingRankingDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [chartRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('ecoDrivingRanking');
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);
	const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({ success: true, warning: true, danger: true });

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const result = await DashboardService.getEcoDrivingRanking({ dateFrom, dateTo });
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [dateFrom, dateTo]);

	const items = data || [];
	const makeSeries = (pick: (d: EcoDrivingRankingDashboardDto) => number) =>
		items.map(d => ({ x: d.driverName, y: pick(d), driver: d.driverName, success: d.successTrips, warning: d.warningTrips, danger: d.dangerTrips }));
	const successSeries = makeSeries(d => d.successTrips);
	const warningSeries = makeSeries(d => d.warningTrips);
	const dangerSeries = makeSeries(d => d.dangerTrips);

	const barWidth = items.length > 0 ? Math.min(60, ((width - 100) / items.length) * 0.55) : 20;

	const toggleSeries = (key: SeriesKey) => setVisible(prev => ({ ...prev, [key]: !prev[key] }));

	const axisEvents = [{
		target: 'tickLabels' as const,
		eventHandlers: {
			onMouseEnter: (evt: any, targetProps: any) => {
				const driver = items.find(d => d.driverName === targetProps.datum);
				if (driver) {
					setTooltip({
						driver: driver.driverName,
						success: driver.successTrips,
						warning: driver.warningTrips,
						danger: driver.dangerTrips,
						x: evt.clientX,
						y: evt.clientY,
					});
				}
				return [];
			},
			onMouseMove: (evt: any) => {
				setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
				return [];
			},
			onMouseLeave: () => { setTooltip(null); return []; },
		},
	}];

	const hoverEvents = [{
		target: 'data' as const,
		eventHandlers: {
			onMouseEnter: (evt: any, targetProps: any) => {
				const d = targetProps.datum;
				setTooltip({ driver: d.driver, success: d.success, warning: d.warning, danger: d.danger, x: evt.clientX, y: evt.clientY });
				return [];
			},
			onMouseMove: (evt: any) => {
				setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
				return [];
			},
			onMouseLeave: () => { setTooltip(null); return []; },
		},
	}];

	const legendItem = (key: SeriesKey, color: string, label: string) => (
		<button
			type="button"
			className={`${styles.legendItem} ${!visible[key] ? styles.legendItemOff : ''}`}
			onClick={() => toggleSeries(key)}
		>
			<span className={styles.legendDot} style={{ background: color }} />
			<span className={styles.legendLabel}>{label}</span>
		</button>
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.eco_driving_ranking' as any) as string}
			subTitleTooltip={t('dashboard.eco_driving_ranking_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<div className={styles.chartContainer}>
				<div className={styles.chartArea} ref={chartRef}>
					{isLoading ? (
						<div className={styles.centerAlign}><LoadingSpinner /></div>
					) : items.length === 0 ? (
						<WidgetNoData />
					) : (
						width > 0 && height > 0 && (
							<VictoryChart
								width={width}
								height={height}
								domainPadding={{ x: Math.max(30, 60 - items.length * 3) }}
								padding={{ top: 15, bottom: 55, left: 50, right: 20 }}
							>
								<VictoryAxis
									tickFormat={(name: string) => getInitials(name)}
									// eslint-disable-next-line
									events={axisEvents}
									style={{
										axis: { stroke: 'transparent' },
										tickLabels: { fontSize: 11, fontFamily: 'Roboto', fill: '#6b7280', cursor: 'pointer' },
									}}
								/>
								<VictoryAxis
									dependentAxis
									style={{
										axis: { stroke: 'transparent' },
										grid: { stroke: '#f5f5f5' },
										tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
									}}
								/>
								<VictoryStack>
									{[
										visible.success && (
											<VictoryBar
												key="success"
												data={successSeries}
												barWidth={barWidth}
												style={{ data: { fill: SUCCESS_COLOR, cursor: 'pointer' } }}
												// eslint-disable-next-line
												events={hoverEvents}
											/>
										),
										visible.warning && (
											<VictoryBar
												key="warning"
												data={warningSeries}
												barWidth={barWidth}
												style={{ data: { fill: WARNING_COLOR, cursor: 'pointer' } }}
												// eslint-disable-next-line
												events={hoverEvents}
											/>
										),
										visible.danger && (
											<VictoryBar
												key="danger"
												data={dangerSeries}
												barWidth={barWidth}
												cornerRadius={{ top: 4 }}
												style={{ data: { fill: DANGER_COLOR, cursor: 'pointer' } }}
												// eslint-disable-next-line
												events={hoverEvents}
											/>
										),
									].filter(Boolean)}
								</VictoryStack>
							</VictoryChart>
						)
					)}
					{tooltip && (
						<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
							<div className={styles.tooltipTitle}>{tooltip.driver}</div>
							{visible.success && (
								<div className={styles.tooltipRow}>
									<span className={styles.tooltipDot} style={{ backgroundColor: SUCCESS_COLOR }} />
									<span className={styles.tooltipRowLabel}>{t('dashboard.eco_driving_success_trips' as any) as string}:</span>
									<span className={styles.tooltipRowValue}>{tooltip.success}</span>
								</div>
							)}
							{visible.warning && (
								<div className={styles.tooltipRow}>
									<span className={styles.tooltipDot} style={{ backgroundColor: WARNING_COLOR }} />
									<span className={styles.tooltipRowLabel}>{t('dashboard.eco_driving_warning_trips')}:</span>
									<span className={styles.tooltipRowValue}>{tooltip.warning}</span>
								</div>
							)}
							{visible.danger && (
								<div className={styles.tooltipRow}>
									<span className={styles.tooltipDot} style={{ backgroundColor: DANGER_COLOR }} />
									<span className={styles.tooltipRowLabel}>{t('dashboard.eco_driving_danger_trips')}:</span>
									<span className={styles.tooltipRowValue}>{tooltip.danger}</span>
								</div>
							)}
						</ChartTooltipPortal>
					)}
				</div>
				<div className={styles.customLegend}>
					{legendItem('success', SUCCESS_COLOR, t('dashboard.eco_driving_success_trips' as any) as string)}
					{legendItem('warning', WARNING_COLOR, t('dashboard.eco_driving_warning_trips' as any) as string)}
					{legendItem('danger', DANGER_COLOR, t('dashboard.eco_driving_danger_trips' as any) as string)}
				</div>
			</div>
		</Box>
	);
};

export default EcoDrivingRanking;
